// A small "i" beside a configuration item. Clicking it opens a panel
// that says what the item is and what its options do.
//
// Opens on click or tap, never hover, so it works the same on a phone.
// Closes on Escape, a press outside or a second click, and focus goes
// back to the icon. The panel is portalled to the body and fixed in
// place so a modal or a scrolling table can't clip it, and its z-index
// sits above every overlay in the apps.
//
// Don't put InfoTip inside a <label>. A label with no `htmlFor` belongs
// to the first button or input inside it, so an icon placed ahead of the
// control takes the label away from it: the control loses its name and
// clicking the label's text opens the panel. Use LabelWithInfo.

import * as React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { cn } from '../lib/cn';
import { bindInfoTipDismiss, placeInfoTip } from '../lib/info-tip-behavior';

export interface InfoTipOption {
  /** The option as the control shows it. */
  name: string;
  /** What choosing it does. */
  description: string;
}

/** Shown by InfoTip. Pages keep these in a typed map per area, not in JSX. */
export interface InfoTipContent {
  title: string;
  /** One or two sentences on what the item is. */
  description: string;
  options?: readonly InfoTipOption[];
  learnMoreHref?: string;
}

export interface InfoTipProps {
  content: InfoTipContent;
  /** Names the item in "About <item>". Defaults to the content's title. */
  label?: string;
  className?: string;
}

// Inline rather than React state: the layout effect writes the real
// position before paint and then focuses the panel, which a hidden
// element can't take.
const UNPLACED: CSSProperties = { top: 0, left: 0, visibility: 'hidden' };

export function InfoTip({ content, label, className }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const place = useCallback(() => {
    if (!button.current || !panel.current) return;
    const { top, left } = placeInfoTip(
      button.current.getBoundingClientRect(),
      { width: panel.current.offsetWidth, height: panel.current.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight },
    );
    panel.current.style.top = `${top}px`;
    panel.current.style.left = `${left}px`;
    panel.current.style.visibility = 'visible';
  }, []);

  const closeAndRefocus = useCallback(() => {
    setOpen(false);
    button.current?.focus({ preventScroll: true });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    panel.current?.focus({ preventScroll: true });
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    return bindInfoTipDismiss(window, {
      isInside: (target) => {
        const node = target as Node | null;
        return !!(panel.current?.contains(node) || button.current?.contains(node));
      },
      onEscape: closeAndRefocus,
      onOutsidePress: () => setOpen(false),
      // A press on another control keeps that control's focus; a press
      // on empty page leaves focus nowhere, so it comes back to the icon.
      onOutsidePressSettled: () => {
        const active = document.activeElement;
        if (!active || active === document.body) button.current?.focus({ preventScroll: true });
      },
      onReposition: (target) => {
        if (!panel.current?.contains(target as Node | null)) place();
      },
    });
  }, [open, place, closeAndRefocus]);

  const onToggle = (e: SyntheticEvent) => {
    // The icon often sits in a clickable row or a <summary>.
    e.preventDefault();
    e.stopPropagation();
    if (open) closeAndRefocus();
    else setOpen(true);
  };

  // The panel sits under <body> in the DOM but under the page in React,
  // so without this a click in it would reach the page's own handlers.
  const stop = (e: SyntheticEvent) => e.stopPropagation();

  // Tab leaves the panel the way it came, rather than falling off the
  // end of <body> and leaving the panel open behind the user.
  const onPanelKeyDown = (e: ReactKeyboardEvent) => {
    e.stopPropagation();
    if (e.key !== 'Tab') return;
    const link = panel.current?.querySelector('a');
    const toLink = link && !e.shiftKey && document.activeElement !== link;
    if (toLink) return;
    e.preventDefault();
    closeAndRefocus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label={`About ${label ?? content.title}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={onToggle}
        className={cn(
          // The ::after widens the tap target without moving the layout.
          "relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full align-text-bottom text-slate-500 transition-colors after:absolute after:-inset-2 after:content-[''] hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-400 dark:hover:text-indigo-300",
          open && 'text-indigo-600 dark:text-indigo-300',
          className,
        )}
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <InfoTipPanel
            ref={panel}
            id={panelId}
            content={content}
            style={UNPLACED}
            onClick={stop}
            onPointerDown={stop}
            onMouseDown={stop}
            onKeyDown={onPanelKeyDown}
          />,
          document.body,
        )}
    </>
  );
}

export interface InfoTipPanelProps {
  id: string;
  content: InfoTipContent;
  style?: CSSProperties;
  onClick?: (e: SyntheticEvent) => void;
  onPointerDown?: (e: SyntheticEvent) => void;
  onMouseDown?: (e: SyntheticEvent) => void;
  onKeyDown?: (e: ReactKeyboardEvent) => void;
}

/** The panel itself. Exported for tests; pages use InfoTip. */
export const InfoTipPanel = forwardRef<HTMLDivElement, InfoTipPanelProps>(function InfoTipPanel({ id, content, ...rest }, ref) {
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  return (
    <div
      ref={ref}
      id={id}
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={-1}
      className="fixed z-[110] max-h-[min(26rem,calc(100dvh-2rem))] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-left text-sm font-normal normal-case tracking-normal text-slate-900 shadow-xl ring-1 ring-slate-900/5 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:ring-white/10"
      {...rest}
    >
      <p id={titleId} className="font-semibold">
        {content.title}
      </p>
      <p id={descriptionId} className="mt-1 text-slate-600 dark:text-slate-300">
        {content.description}
      </p>
      {content.options && content.options.length > 0 && (
        <dl className="mt-2 space-y-1.5 border-t border-slate-100 pt-2 dark:border-slate-800">
          {content.options.map((option) => (
            <div key={option.name}>
              <dt className="font-medium">{option.name}</dt>
              <dd className="text-slate-600 dark:text-slate-300">{option.description}</dd>
            </div>
          ))}
        </dl>
      )}
      {content.learnMoreHref && (
        <a
          href={content.learnMoreHref}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block rounded text-indigo-700 underline hover:text-indigo-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300 dark:hover:text-indigo-200"
        >
          Learn more
        </a>
      )}
    </div>
  );
});

export interface LabelWithInfoProps {
  /** The `id` of the control this labels. */
  htmlFor?: string;
  children: ReactNode;
  info: InfoTipContent;
  /** About-name for the icon when `info.title` isn't the label's own text. */
  infoLabel?: string;
  className?: string;
  labelClassName?: string;
}

/** A field's label with its icon beside it, the icon outside the <label>. */
export function LabelWithInfo({ htmlFor, children, info, infoLabel, className, labelClassName }: LabelWithInfoProps) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <label htmlFor={htmlFor} className={labelClassName}>
        {children}
      </label>
      <InfoTip content={info} label={infoLabel} />
    </span>
  );
}
